import { Formik, Form, getIn } from "formik";
import TextField from "../components/common/TextField";
import Button from "../components/common/Button";
import Dropdown from "../components/common/Dropdown";
import { orderTypeOptions, payoutOptions } from "../configs";
import SchedulePicker from "../components/common/SchedulePicker";
import { useFetchAllCitiesQuery } from "../store";
import { formatCitiesToOption } from "../formatters";
import { ReactNode } from "react";
import {
  FaSave,
  FaStore,
  FaMapMarkerAlt,
  FaMoneyBillWave,
  FaUserTie,
  FaClock,
  FaList,
  FaCog,
} from "react-icons/fa";

interface RestaurantFormProps {
  initialValues: any;
  validationSchema: any;
  onSubmit: (
    values: any,
    formikHelpers: {
      resetForm: () => void;
    }
  ) => Promise<void>;
  isLoading?: boolean;
}

const RestaurantForm: React.FC<RestaurantFormProps> = ({
  initialValues,
  validationSchema,
  onSubmit,
  isLoading,
}) => {
  const { data: cities } = useFetchAllCitiesQuery();

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        setFieldValue,
        setFieldTouched,
        isSubmitting,
      }) => {
        return (
          <Form className="w-full max-w-6xl mx-auto pb-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left Column */}
              <div className="space-y-6">
                {/* Basic Info */}
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                  <div className="flex items-center gap-2 mb-4 text-primary border-b pb-2">
                    <FaStore />
                    <h3 className="font-semibold text-lg">
                      Informations générales
                    </h3>
                  </div>
                  <div className="space-y-4">
                    <TextField
                      id="displayName"
                      label="Nom du restaurant"
                      value={values.displayName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      errors={errors}
                      touched={touched}
                      placeholder="Ex: Le Petit Bistro"
                    />
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <textarea
                        id="description"
                        rows={3}
                        value={values.description}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all text-sm"
                        placeholder="Une courte description du restaurant..."
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <TextField
                        id="email"
                        label="Email"
                        value={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        errors={errors}
                        touched={touched}
                        placeholder="contact@restaurant.com"
                      />
                      <TextField
                        id="password"
                        label="Mot de passe"
                        value={values.password}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        errors={errors}
                        touched={touched}
                        placeholder="••••••••"
                        hideable
                      />
                    </div>
                    <Dropdown
                      id="city"
                      value={values.city}
                      setFieldValue={setFieldValue}
                      setFieldTouched={setFieldTouched}
                      options={formatCitiesToOption(cities || [])}
                      errors={errors}
                      touched={touched}
                      label="Ville d'opération"
                      className="w-full"
                    />
                  </div>
                </div>

                {/* Referent */}
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                  <div className="flex items-center gap-2 mb-4 text-primary border-b pb-2">
                    <FaUserTie />
                    <h3 className="font-semibold text-lg">Référent & Légal</h3>
                  </div>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <TextField
                        id="referant.firstName"
                        label="Prénom"
                        value={values.referant.firstName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        errors={errors}
                        touched={touched}
                        placeholder="Jean"
                      />
                      <TextField
                        id="referant.lastName"
                        label="Nom"
                        value={values.referant.lastName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        errors={errors}
                        touched={touched}
                        placeholder="Dupont"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <TextField
                        id="referant.phone"
                        label="Téléphone"
                        value={values.referant.phone}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        errors={errors}
                        touched={touched}
                        placeholder="06 12 34 56 78"
                      />
                      <TextField
                        id="referant.siret"
                        label="SIRET"
                        value={values.referant.siret}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        errors={errors}
                        touched={touched}
                        placeholder="123 456 789 00012"
                      />
                    </div>
                  </div>
                </div>

                {/* Payout & Settings */}
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                  <div className="flex items-center gap-2 mb-4 text-primary border-b pb-2">
                    <FaMoneyBillWave />
                    <h3 className="font-semibold text-lg">
                      Commission & Paramètres
                    </h3>
                  </div>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <Dropdown
                        id="payout.type"
                        value={values.payout.type}
                        setFieldValue={setFieldValue}
                        setFieldTouched={setFieldTouched}
                        options={payoutOptions}
                        errors={errors}
                        touched={touched}
                        label="Type de commission"
                        className="w-full"
                      />
                      <TextField
                        id="payout.value"
                        label="Valeur (%)"
                        value={values.payout.value}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        errors={errors}
                        touched={touched}
                        placeholder="Ex: 20"
                      />
                    </div>
                    <div className="pt-2 border-t border-gray-100">
                      <TextField
                        id="settings.allergensLink"
                        label="Lien carte des allergènes"
                        value={values.settings.allergensLink}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        errors={errors}
                        touched={touched}
                        placeholder="https://..."
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-6">
                {/* Address */}
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                  <div className="flex items-center gap-2 mb-4 text-primary border-b pb-2">
                    <FaMapMarkerAlt />
                    <h3 className="font-semibold text-lg">Localisation</h3>
                  </div>
                  <div className="space-y-4">
                    <TextField
                      id="address.street"
                      label="Rue"
                      value={values.address.street}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      errors={errors}
                      touched={touched}
                      placeholder="123 Avenue de la République"
                    />
                    <div className="grid grid-cols-2 gap-4">
                      <TextField
                        id="address.zipCode"
                        label="Code postal"
                        value={values.address.zipCode}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        errors={errors}
                        touched={touched}
                        placeholder="75001"
                      />
                      <TextField
                        id="address.city"
                        label="Ville"
                        value={values.address.city}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        errors={errors}
                        touched={touched}
                        placeholder="Paris"
                      />
                    </div>
                    <TextField
                      id="address.country"
                      label="Pays"
                      value={values.address.country}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      errors={errors}
                      touched={touched}
                      placeholder="France"
                    />
                    <div className="bg-gray-50 p-4 rounded-md">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">
                        Coordonnées GPS
                      </h4>
                      <div className="grid grid-cols-2 gap-4">
                        <TextField
                          id="address.coordinates[0]"
                          label="Longitude"
                          value={values.address.coordinates[0]}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          errors={errors}
                          touched={touched}
                          placeholder="2.3522"
                        />
                        <TextField
                          id="address.coordinates[1]"
                          label="Latitude"
                          value={values.address.coordinates[1]}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          errors={errors}
                          touched={touched}
                          placeholder="48.8566"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Order Types */}
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                  <div className="flex items-center gap-2 mb-4 text-primary border-b pb-2">
                    <FaList />
                    <h3 className="font-semibold text-lg">
                      Types de commande acceptés
                    </h3>
                  </div>
                  <div className="flex flex-wrap gap-4">
                    {orderTypeOptions.map((opt) => (
                      <label
                        key={opt.value}
                        className={`flex items-center gap-2 px-4 py-2 rounded-full border cursor-pointer transition-colors ${
                          values.orderTypes.includes(opt.value)
                            ? "bg-primary text-white border-primary"
                            : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="hidden"
                          checked={values.orderTypes.includes(opt.value)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFieldValue("orderTypes", [
                                ...values.orderTypes,
                                opt.value,
                              ]);
                            } else {
                              setFieldValue(
                                "orderTypes",
                                values.orderTypes.filter(
                                  (t: string) => t !== opt.value
                                )
                              );
                            }
                          }}
                        />
                        <span className="font-medium">{opt.label}</span>
                      </label>
                    ))}
                  </div>
                  {getIn(errors, "orderTypes") &&
                    getIn(touched, "orderTypes") && (
                      <div className="text-sm text-red-500 mt-2">
                        {getIn(errors, "orderTypes") as ReactNode}
                      </div>
                    )}
                </div>

                {/* Schedule */}
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                  <div className="flex items-center gap-2 mb-4 text-primary border-b pb-2">
                    <FaClock />
                    <h3 className="font-semibold text-lg">
                      Horaires d&apos;ouverture
                    </h3>
                  </div>
                  <SchedulePicker />
                </div>
              </div>
            </div>

            <div className="mt-8 flex justify-end">
              <Button
                className="w-full sm:w-auto px-8 py-3 bg-primary hover:bg-opacity-90 text-white rounded-lg shadow-md font-bold text-lg flex items-center justify-center gap-2 transition-all"
                disabled={isSubmitting || isLoading}
                type="submit"
              >
                <FaSave />
                {isLoading ? "Enregistrement..." : "Enregistrer le restaurant"}
              </Button>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default RestaurantForm;
